import type { OrgInquiryCounts, OrgMembership, OrgRole } from '@/types/organization'

/**
 * One row of the members list.
 *
 * `counts` is `null` when the viewer is not an OWNER/ADMIN — the activity rollup
 * is manager-only, so a plain MEMBER gets the roster without anyone's numbers.
 * That is the single switch that separates the admin and member views; the
 * management controls key off `canManage` below.
 */
export interface OrgMemberRow {
  membership: OrgMembership
  counts: OrgInquiryCounts | null
  isSelf: boolean
}

export interface OrgMembersListBaseProps {
  rows: OrgMemberRow[]
  isLoading: boolean
  /** OWNER|ADMIN: may remove members and open per-member activity. */
  canManage: boolean
  /** OWNER only: may change another member's role. */
  canChangeRoles: boolean
  /** The viewer's own role, needed for the ADMIN-cannot-touch-an-OWNER rules. */
  viewerRole?: OrgRole
  selectedUserId: string | null
  /** user_id of the row with a mutation in flight, so it can show a busy state. */
  pendingUserId: string | null
  onViewActivity: (row: OrgMemberRow) => void
  onChangeRole: (row: OrgMemberRow, role: OrgRole) => void
  onRemove: (row: OrgMemberRow) => void
}

export const ROLE_OPTIONS: OrgRole[] = ['OWNER', 'ADMIN', 'MEMBER']

export const ROLE_LABELS: Record<OrgRole, string> = {
  OWNER: 'Owner',
  ADMIN: 'Admin',
  MEMBER: 'Member',
}

/**
 * Mirrors the backend rule in `remove_member.py`: OWNER/ADMIN may remove members,
 * an ADMIN may not remove an OWNER, and nobody removes themselves from here.
 *
 * "Last owner" is deliberately NOT modelled client-side — the frontend cannot see
 * the authoritative owner count for a paginated list, so we let the server answer
 * `Cannot remove the last owner` and surface that message.
 */
export const canRemoveRow = (row: OrgMemberRow, props: OrgMembersListBaseProps): boolean => {
  if (!props.canManage) return false
  if (row.isSelf) return false
  if (row.membership.role === 'OWNER' && props.viewerRole !== 'OWNER') return false
  return true
}

/** Mirrors `update_member.py`: role changes are OWNER-only. */
export const canChangeRoleOfRow = (
  row: OrgMemberRow,
  props: OrgMembersListBaseProps,
): boolean => props.canChangeRoles && !row.isSelf

/** Activity is manager-only, so a row is only clickable when counts were loaded. */
export const canViewActivity = (props: OrgMembersListBaseProps): boolean => props.canManage
